/**
 * Farbmischung in OKLab — nachgebaut, weil `color-mix(in oklab, …)` erst ab Safari 16.2
 * existiert und die App auf iPadOS 15 laufen muss. Die Design-Vorlage benutzt die
 * CSS-Funktion für genau eine Stelle (den Seitenhintergrund); hier wird derselbe Wert
 * vorab gerechnet, damit das Ergebnis identisch aussieht.
 */

type Rgb = [number, number, number];

function parseHex(hex: string): Rgb {
  const h = hex.replace('#', '');
  const full =
    h.length === 3
      ? h
          .split('')
          .map((c) => c + c)
          .join('')
      : h;
  return [
    parseInt(full.slice(0, 2), 16) / 255,
    parseInt(full.slice(2, 4), 16) / 255,
    parseInt(full.slice(4, 6), 16) / 255,
  ];
}

function toHex([r, g, b]: Rgb): string {
  const c = (v: number) =>
    Math.max(0, Math.min(255, Math.round(v * 255)))
      .toString(16)
      .padStart(2, '0');
  return '#' + c(r) + c(g) + c(b);
}

/** sRGB-Kanal → linearer Anteil (Gamma raus). */
const toLinear = (v: number) => (v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4));

/** Linearer Anteil → sRGB-Kanal (Gamma rein). */
const toSrgb = (v: number) => (v <= 0.0031308 ? v * 12.92 : 1.055 * Math.pow(v, 1 / 2.4) - 0.055);

function srgbToOklab([r, g, b]: Rgb): Rgb {
  const lr = toLinear(r);
  const lg = toLinear(g);
  const lb = toLinear(b);

  const l = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb);
  const m = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb);
  const s = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb);

  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  ];
}

function oklabToSrgb([L, a, bb]: Rgb): Rgb {
  const l = Math.pow(L + 0.3963377774 * a + 0.2158037573 * bb, 3);
  const m = Math.pow(L - 0.1055613458 * a - 0.0638541728 * bb, 3);
  const s = Math.pow(L - 0.0894841775 * a - 1.291485548 * bb, 3);

  return [
    toSrgb(4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s),
    toSrgb(-1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s),
    toSrgb(-0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s),
  ];
}

/**
 * Entspricht `color-mix(in oklab, <a> <weightA>%, <b>)`.
 *
 * @param a        Erste Farbe als Hex.
 * @param weightA  Anteil der ersten Farbe, 0…1.
 * @param b        Zweite Farbe als Hex.
 */
export function mixOklab(a: string, weightA: number, b: string): string {
  const A = srgbToOklab(parseHex(a));
  const B = srgbToOklab(parseHex(b));
  const w = Math.max(0, Math.min(1, weightA));
  const mixed: Rgb = [
    A[0] * w + B[0] * (1 - w),
    A[1] * w + B[1] * (1 - w),
    A[2] * w + B[2] * (1 - w),
  ];
  return toHex(oklabToSrgb(mixed));
}
